import { memo, useCallback, useMemo } from 'react';
import { ManualArea, StationSpec } from '@thive/fddm-types';

import { GapHorizontalIcon } from '@/assets/icons';
import { SelectInput } from '@/modules/common/components/inputs';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumUtils } from '@/modules/common/utils';
import { getCommonNumber, getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';
import { useTranslatedOptions } from '@/modules/common/hooks';

const LOAD_HANDLING_DURATION_STRATEGY_IN_SIMULATION_OPTIONS: {
  labelKey: string;
  value: StationSpec['loadHandlingDurationStrategyInSimulation'];
}[] = [
  {
    labelKey: 'interface:properties.area.load_handling_duration_strategy_options.by_height',
    value: 'BY_HEIGHT',
  },
  {
    labelKey: 'interface:properties.area.load_handling_duration_strategy_options.by_override',
    value: 'BY_OVERRIDE',
  },
];

interface Props {
  selectedManualAreas: ManualArea[];
}

type AreaStationSpec = ManualArea['stationSpec'];

const canEditLoadDuration = (loadHandlingType: ManualArea['loadHandlingType']) =>
  loadHandlingType === 'INTAKE' || loadHandlingType === 'INTAKE_AND_DELIVERY';

const canEditUnloadDuration = (loadHandlingType: ManualArea['loadHandlingType']) =>
  loadHandlingType === 'DELIVERY' || loadHandlingType === 'INTAKE_AND_DELIVERY';

const isByOverride = ({ stationSpec }: ManualArea): boolean =>
  stationSpec.loadHandlingDurationStrategyInSimulation === 'BY_OVERRIDE';

/** @description Load time applies to 'By override' areas that handle intake. */
const hasLoadDuration = (area: ManualArea): boolean =>
  isByOverride(area) && canEditLoadDuration(area.loadHandlingType);

/** @description Unload time applies to 'By override' areas that handle delivery. */
const hasUnloadDuration = (area: ManualArea): boolean =>
  isByOverride(area) && canEditUnloadDuration(area.loadHandlingType);

function ManualStationSpecPropertiesComponent({ selectedManualAreas }: Props) {
  const loadHandlingDurationStrategyInSimulationOptions = useTranslatedOptions(
    LOAD_HANDLING_DURATION_STRATEGY_IN_SIMULATION_OPTIONS,
  );
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  // null means the selected areas have different values ('Mixed')
  const commonValues = useMemo(() => {
    const specs = selectedManualAreas.map(({ stationSpec }) => stationSpec);

    return {
      zMm: getCommonNumber(specs.map(({ z }) => NumUtils.metersToMmRounded(z))),
      strategy: getCommonOption(specs.map((spec) => spec.loadHandlingDurationStrategyInSimulation)),
    };
  }, [selectedManualAreas]);

  // Load time shows when any selected area uses it; only editable when all of them do
  const loadDurationAreas = useMemo(
    () => selectedManualAreas.filter(hasLoadDuration),
    [selectedManualAreas],
  );
  const isLoadDurationPartialSelection = loadDurationAreas.length !== selectedManualAreas.length;
  // null shows 'Mixed'
  const loadDuration = useMemo(
    () =>
      getCommonNumber(
        loadDurationAreas.map(
          ({ stationSpec }) => stationSpec.loadDurationOverrideInSimulation ?? 0,
        ),
      ),
    [loadDurationAreas],
  );

  // Unload time shows when any selected area uses it; only editable when all of them do
  const unloadDurationAreas = useMemo(
    () => selectedManualAreas.filter(hasUnloadDuration),
    [selectedManualAreas],
  );
  const isUnloadDurationPartialSelection =
    unloadDurationAreas.length !== selectedManualAreas.length;
  // null shows 'Mixed'
  const unloadDuration = useMemo(
    () =>
      getCommonNumber(
        unloadDurationAreas.map(
          ({ stationSpec }) => stationSpec.unloadDurationOverrideInSimulation ?? 0,
        ),
      ),
    [unloadDurationAreas],
  );

  /** Applies the same station spec change to every selected area in one update (one undo step). */
  const updateStationSpecs = useCallback(
    (updateSpec: (spec: AreaStationSpec) => AreaStationSpec) => {
      const updatedAreas = createEmptyAreas();
      selectedManualAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          stationSpec: updateSpec(area.stationSpec),
        });
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, selectedManualAreas],
  );

  const onZChange = useCallback(
    (newValueMm: number) => {
      if (Number.isNaN(newValueMm)) return;

      const newValue = NumUtils.mmToMeters(newValueMm);

      fddmSvc.setPreference('manualArea.stationSpec.z', newValue);
      updateStationSpecs((spec) => ({ ...spec, z: newValue }));
    },
    [fddmSvc, updateStationSpecs],
  );

  const onStrategyChange = useCallback(
    (newValue: StationSpec['loadHandlingDurationStrategyInSimulation']) => {
      fddmSvc.setPreference(
        'manualArea.stationSpec.loadHandlingDurationStrategyInSimulation',
        newValue,
      );
      updateStationSpecs((spec) => ({
        ...spec,
        loadHandlingDurationStrategyInSimulation: newValue,
      }));
    },
    [fddmSvc, updateStationSpecs],
  );

  const onLoadDurationChange = useCallback(
    (newValue: number) => {
      if (isLoadDurationPartialSelection || Number.isNaN(newValue)) return;

      fddmSvc.setPreference('manualArea.stationSpec.loadDurationOverrideInSimulation', newValue);
      updateStationSpecs((spec) => ({ ...spec, loadDurationOverrideInSimulation: newValue }));
    },
    [fddmSvc, isLoadDurationPartialSelection, updateStationSpecs],
  );

  const onUnloadDurationChange = useCallback(
    (newValue: number) => {
      if (isUnloadDurationPartialSelection || Number.isNaN(newValue)) return;

      fddmSvc.setPreference('manualArea.stationSpec.unloadDurationOverrideInSimulation', newValue);
      updateStationSpecs((spec) => ({ ...spec, unloadDurationOverrideInSimulation: newValue }));
    },
    [fddmSvc, isUnloadDurationPartialSelection, updateStationSpecs],
  );

  if (selectedManualAreas.length === 0) return null;

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.load_elevation'>
        <NumberInput
          value={commonValues.zMm}
          unit='mm'
          prefix={<GapHorizontalIcon />}
          sx={{ width: '100%' }}
          onChange={onZChange}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.load_duration_strategy'>
        <SelectInput
          options={loadHandlingDurationStrategyInSimulationOptions}
          onChange={onStrategyChange}
          value={commonValues.strategy}
          showMixed
        />
      </PropertiesLabel>

      {loadDurationAreas.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.load_duration'>
          <DisabledReasonTooltip
            reasonKey={
              isLoadDurationPartialSelection
                ? 'interface:properties.area.partial_selection.only_by_override_intake'
                : undefined
            }
          >
            <NumberInput
              // undefined (not false) keeps NumberInput's own read-only-mode check
              disabled={isLoadDurationPartialSelection ? true : undefined}
              value={loadDuration}
              unit='sec'
              prefix={<GapHorizontalIcon />}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onLoadDurationChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}

      {unloadDurationAreas.length > 0 && (
        <PropertiesLabel i18nkey='interface:properties.area.unload_duration'>
          <DisabledReasonTooltip
            reasonKey={
              isUnloadDurationPartialSelection
                ? 'interface:properties.area.partial_selection.only_by_override_delivery'
                : undefined
            }
          >
            <NumberInput
              disabled={isUnloadDurationPartialSelection ? true : undefined}
              value={unloadDuration}
              unit='sec'
              prefix={<GapHorizontalIcon />}
              sx={{ width: '100%' }}
              minValue={0}
              onChange={onUnloadDurationChange}
            />
          </DisabledReasonTooltip>
        </PropertiesLabel>
      )}
    </>
  );
}

export const StationSpecProperties = memo(ManualStationSpecPropertiesComponent);
